/**
 * Inventory dashboard — warehouse value, reorder / expiry / aging alerts + charts.
 * Rebuilt on the Tailwind design system: PageHeader, StatCard (count-up KPIs),
 * ui/Card, StatusBadge/Tag, skeleton loading and EmptyState. All queries, data
 * mapping, navigation and chart series are unchanged (ECharts kept, brand-tuned).
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { Container, AlertTriangle, Flame, Clock, PackageCheck } from 'lucide-react';
import { request } from '../../api/client';
import { themedCatAxis, themedValAxis, themedLegendText, themedLabel } from '../../lib/chartTheme';
import {
  PageHeader, StatCard, Card, Tag, Alert, EmptyState, SkeletonStats, Skeleton, theme,
} from '../../ui';
import { CountUp } from '../../ui/motion';

const inr = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export function InventoryDashboard() {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-inventory'],
    queryFn: () => request<any>({ url: '/dashboard/inventory' }),
  });
  const { data: reorder } = useQuery({ queryKey: ['stock-reorder'], queryFn: () => request<any[]>({ url: '/stock/alerts/reorder' }) });
  const { data: expiry } = useQuery({ queryKey: ['stock-expiry'], queryFn: () => request<any[]>({ url: '/stock/alerts/expiry' }) });
  const { data: aging } = useQuery({ queryKey: ['stock-aging'], queryFn: () => request<any[]>({ url: '/stock/alerts/aging' }) });

  const header = <PageHeader title="Inventory dashboard" subtitle="Stock value, reorder & expiry alerts across every warehouse." icon={<Container />} />;

  if (isError) {
    return (
      <div>
        {header}
        <Alert type="error" title="Couldn't load the inventory dashboard" description="Please retry in a moment." />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div>
        {header}
        <SkeletonStats count={4} className="mb-4" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-56 rounded-card" />
          <Skeleton className="h-56 rounded-card" />
        </div>
      </div>
    );
  }

  const byWh = data?.byWh ?? [];
  const agingBuckets = data?.aging ?? {};
  const totalValue = byWh.reduce((s: number, w: any) => s + Number(w.value ?? 0), 0);
  const reorderN = reorder?.length ?? 0;
  const expiryN = expiry?.length ?? 0;
  const agingN = aging?.length ?? 0;

  const whValueOption = {
    grid: { left: 50, right: 20, top: 30, bottom: 40 },
    xAxis: { type: 'category', data: byWh.map((w: any) => w.code), ...themedCatAxis(token) },
    yAxis: {
      type: 'value', ...themedValAxis(token),
      axisLabel: { color: token.colorTextTertiary, formatter: (v: number) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${Math.round(v / 1000)}k`) },
    },
    tooltip: { trigger: 'axis', formatter: (p: any) => `<b>${p[0].data?.name ?? p[0].name}</b><br/>${p[0].marker} ${inr(p[0].value)}<br/>${p[0].data?.skus ?? ''} SKUs` },
    series: [{ type: 'bar', barMaxWidth: 40, itemStyle: { color: '#339022', borderRadius: [4, 4, 0, 0] }, data: byWh.map((w: any) => ({ value: Number(w.value ?? 0), skus: w.skus, name: w.name })) }],
  };

  const agingOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} units ({d}%)' },
    legend: { orient: 'vertical', right: 0, top: 'middle', textStyle: themedLegendText(token) },
    series: [{
      type: 'pie', radius: ['42%', '70%'], label: { formatter: '{b}\n{d}%', ...themedLabel(token) },
      itemStyle: { borderColor: token.colorBgContainer, borderWidth: 2 },
      data: [
        { name: '0–30 days', value: Number(agingBuckets.a0_30 ?? 0), itemStyle: { color: '#339022' } },
        { name: '31–60 days', value: Number(agingBuckets.a31_60 ?? 0), itemStyle: { color: '#1677FF' } },
        { name: '61–90 days', value: Number(agingBuckets.a61_90 ?? 0), itemStyle: { color: '#E7B22C' } },
        { name: '>90 days', value: Number(agingBuckets.a_gt90 ?? 0), itemStyle: { color: '#D9363E' } },
      ],
    }],
  };

  return (
    <div>
      {header}

      {/* KPI row */}
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Inventory Value" tone="brand" icon={<Container />} onClick={() => navigate('/inventory/stock')}
          value={<CountUp value={totalValue} format={inr} />} />
        <StatCard label="Reorder Alerts" tone={reorderN > 0 ? 'danger' : 'neutral'} icon={<AlertTriangle />} onClick={() => navigate('/inventory/stock')}
          value={<CountUp value={reorderN} />} hint={reorderN > 0 ? 'Action needed' : 'All stocked'} />
        <StatCard label="Expiring ≤ 90 days" tone={expiryN > 0 ? 'warning' : 'neutral'} icon={<Clock />} onClick={() => navigate('/inventory/stock')}
          value={<CountUp value={expiryN} />} hint="batches" />
        <StatCard label="Aged > 90 days" tone={agingN > 0 ? 'danger' : 'neutral'} icon={<Flame />} onClick={() => navigate('/inventory/stock')}
          value={<CountUp value={agingN} />} hint="batches" />
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-[1.4fr_1fr]">
        <Card title="Value by warehouse" extra="Click bar → stock">
          <ReactECharts option={whValueOption} style={{ height: 200 }} onEvents={{ click: () => navigate('/inventory/stock') }} />
        </Card>
        <Card title="Stock age">
          <ReactECharts option={agingOption} style={{ height: 200 }} />
        </Card>
      </div>

      {/* Alert lists */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card title={`Reorder alerts (${reorderN})`}>
          {reorderN === 0 ? (
            <EmptyState compact icon={<PackageCheck />} title="No reorder alerts" description="Every SKU is above its reorder level." />
          ) : (
            <div className="divide-y divide-line">
              {(reorder ?? []).slice(0, 8).map((r: any) => (
                <div key={`${r.sku}-${r.warehouse_id ?? ''}`} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0 truncate"><b className="text-content">{r.sku}</b> <span className="text-muted">— {r.name}</span>{r.warehouse ? <Tag tone="neutral" className="ml-1.5">{r.warehouse}</Tag> : null}</span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Tag tone="red">{r.available} avl</Tag>
                    <Tag tone="neutral">{r.reorder_level} reorder</Tag>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title={`Expiring batches (${expiryN})`}>
          {expiryN === 0 ? (
            <EmptyState compact icon={<Clock />} title="Nothing expiring soon" description="No batches expiring within 90 days." />
          ) : (
            <div className="divide-y divide-line">
              {(expiry ?? []).slice(0, 8).map((r: any) => (
                <div key={`${r.sku}-${r.batch_no}`} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0 truncate"><b className="text-content">{r.sku}</b> <span className="text-muted">· batch {r.batch_no} · {r.warehouse}</span></span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Tag tone={r.days_to_expiry < 30 ? 'red' : 'gold'}>{r.days_to_expiry} d</Tag>
                    <Tag tone="neutral">{r.available} units</Tag>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
